"use client"
import { ClientOnly } from "@/components/ClientOnly"
import { api } from "@/components/TRPCProvider"
import { useDisclosure } from "@/lib/hooks/useDisclosure"
import { createAssetUrl } from "@/lib/s3"
import { REPORT_TYPE } from "@/lib/static/report-type"
import { cn } from "@/lib/tailwind"
import dayjs from "dayjs"
import { CloudDownload, Folder, FolderCheck, Share } from "lucide-react"
import Image from "next/image"
import { DownloadFile } from "../DownloadFile"
import { EditorContent } from "../EditorContent"
import { LinkButton } from "../LinkButton"
import { MaybeLink } from "../MaybeLink"
import { ShareContent } from "../ShareContent"
import { TooltipIconButton } from "../TooltipIconButton"
import { SaveToLibrary } from "../save-to-library"
import { Modal } from "../ui/Modal"
import type { ReportItemType } from "./fields"
import { IconButton } from "../ui/IconButton"

type ReportItemSize = "sm" | "md" | undefined

interface Props {
  report: ReportItemType
  size?: ReportItemSize
}

export function ReportItem({ report, size = "md" }: Props) {
  const modalProps = useDisclosure()
  const { data: isTrialing, isLoading } = api.user.isTrialing.useQuery()
  const isBlocked = isTrialing && (!!report.downloadPdf || !!report.downloadPpt) && report.isBlockedForTrialers
  const hasBlockedPpt = !!report.downloadPpt && isBlocked
  const isSaved = report.projectItems.length > 0
  return (
    <div>
      <MaybeButton
        className={cn("relative h-max space-y-2 text-left", (report.downloadPdf || hasBlockedPpt) && "group")}
        onClick={!isLoading && isBlocked && !report.downloadPdf ? modalProps.onOpen : undefined}
      >
        <MaybeLink
          className="aspect-video cursor-pointer"
          href={!isLoading && !isBlocked && !!report.downloadPdf ? `/reports/${report.id}` : undefined}
        >
          <Image
            src={createAssetUrl(report.cover)!}
            width={450}
            height={300}
            alt="image cover"
            className="w-full rounded-xl object-cover group-hover:opacity-90"
          />
        </MaybeLink>
        {!isLoading && !isBlocked && !report.downloadPdf && (
          <div className="absolute top-4 left-4 z-10 rounded-full border border-green-600 bg-green-500 px-2 py-1 font-medium text-white">
            Download only
          </div>
        )}
        {!isLoading && isBlocked && (
          <div className="absolute top-4 left-4 z-10 rounded-md bg-orange-500 px-2 py-1 text-white">Available on paid plan</div>
        )}

        <div className="space-y-1">
          <MaybeLink
            href={!isLoading && !isBlocked && report.downloadPdf ? `/reports/${report.id}` : undefined}
            className={cn(
              "font-bold group-hover:opacity-70",
              size === "md" ? "text-lg" : "text-sm",
              report.downloadPdf || hasBlockedPpt ? "cursor-pointer" : "cursor-default",
            )}
          >
            {report.title}
          </MaybeLink>

          <MaybeLink href={!isLoading && !isBlocked && report.downloadPdf ? `/reports/${report.id}` : undefined}>
            <EditorContent
              className={cn(size === "sm" ? "text-xs" : "text-sm", report.downloadPdf || hasBlockedPpt ? "pointer" : "default")}
              content={report.description}
            />
          </MaybeLink>
          <div className="flex w-full items-center justify-between">
            <div className={cn("flex items-center space-x-1 uppercase opacity-60", size === "sm" ? "text-xxs" : "text-sm")}>
              <p>{dayjs(report.publishedAt).format("MM/YYYY")}</p>
              <p>·</p>
              <p>{report.types?.map((type) => REPORT_TYPE[type]).join(" · ")}</p>
            </div>

            {!isLoading && !isBlocked && (
              <ClientOnly>
                <div className="hidden space-x-1 md:flex">
                  {report.downloadPdf && (
                    <ShareContent
                      type={"REPORT"}
                      title={report.title}
                      imageUrl={createAssetUrl(report.cover)}
                      redirectUrl={`/reports/${report.id}`}
                      resourceId={report.id}
                    >
                      <TooltipIconButton
                        tooltipProps={{ label: "Share" }}
                        size={size === "sm" ? "xs" : "sm"}
                        variant="ghost"
                        aria-label="share"
                        icon={<Share size={size === "md" ? 16 : 14} />}
                      />
                    </ShareContent>
                  )}
                  <SaveToLibrary resourceId={report.id} type="REPORT" content={report.title}>
                    <IconButton
                      // tooltipProps={{ label: "Save to project" }}
                      size={size === "md" ? "sm" : "xs"}
                      variant="ghost"
                      aria-label="save"
                      icon={
                        isSaved ? (
                          <FolderCheck className="text-primary" size={size === "md" ? 16 : 14} />
                        ) : (
                          <Folder size={size === "md" ? 16 : 14} />
                        )
                      }
                    />
                  </SaveToLibrary>
                  <DownloadFile
                    pdf={report.downloadPdf}
                    ppt={report.downloadPpt}
                    content={report.title}
                    resourceId={report.id}
                    resource="REPORT"
                  >
                    <IconButton
                      // tooltipProps={{ label: "Download" }}
                      variant="ghost"
                      size={size === "md" ? "sm" : "xs"}
                      aria-label="download"
                      icon={<CloudDownload size={size === "md" ? 16 : 14} />}
                    />
                  </DownloadFile>
                </div>
              </ClientOnly>
            )}
          </div>
        </div>
      </MaybeButton>

      <Modal {...modalProps} title={`The ${report.title} is available to clients only.`}>
        <div className="flex flex-col items-center space-y-4 pb-2">
          <Image
            alt="available on paid plan only"
            src="/payment-required.png"
            width={300}
            height={200}
            className="h-[200px] w-full object-contain"
          />
          <p>Upgrade your trial to a permanent account now and get instant access!</p>
          <LinkButton variant="primary" href="/trial-join-plan">
            Upgrade
          </LinkButton>
        </div>
      </Modal>
    </div>
  )
}

function MaybeButton({
  children,
  onClick,
  className,
}: { children: React.ReactNode; onClick?: (() => void) | undefined; className?: string }) {
  if (!onClick) return <div className={className}>{children}</div>
  return (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  )
}
